import React, { useState } from 'react'
import { TouchableOpacity, View } from 'react-native'
import Animated, { FadeInUp, FadeOutUp } from 'react-native-reanimated'
import { useTutorNoShow } from '../TutorNoShowContext'
import TextM from '@common/components/text/TextM'
import { theme } from '@common/theme/theme'

/**
 * Compact banner shown in header when tutor is absent
 * Clickable to collapse/expand, auto-expands at key moments
 */
export function WaitingBanner() {
    const context = useTutorNoShow()
    const [isCollapsed, setIsCollapsed] = useState(false)

    // Guard: return null if provider is not present (e.g., trial sessions)
    if (!context) return null

    const { shouldShowBanner, minutesSinceStart, isSearching, announcedArrival, tutorBusyElsewhere } = context

    // Auto-expand when entering search phase (important moment)
    // ⚠️ Must be BEFORE conditional return to respect Rules of Hooks
    React.useEffect(() => {
        if (isSearching) {
            setIsCollapsed(false)
        }
    }, [isSearching])

    if (!shouldShowBanner) return null

    const isEarlyArrival = minutesSinceStart < 0

    // Collapsed view - minimal, 1 line
    if (isCollapsed) {
        return (
            <Animated.View
                entering={FadeInUp.duration(200)}
                exiting={FadeOutUp.duration(200)}
                style={{
                    position: 'absolute',
                    top: 50,
                    left: 0,
                    right: 0,
                    alignItems: 'center',
                    zIndex: 99999,
                    elevation: 99999,
                }}
            >
                <TouchableOpacity
                    onPress={() => setIsCollapsed(false)}
                    style={{
                        backgroundColor: '#FF8C42',
                        borderRadius: 20,
                        paddingVertical: 6,
                        paddingHorizontal: 15,
                        borderWidth: 1,
                        borderColor: '#FF6B1A',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <TextM fontSize={13} color={theme.colors.white} fontWeight="Medium">
                        ⏰ En attente du tuteur...
                    </TextM>
                </TouchableOpacity>
            </Animated.View>
        )
    }

    // Expanded view - compact, 2 lines max
    return (
        <Animated.View
            entering={FadeInUp.duration(300)}
            exiting={FadeOutUp.duration(300)}
            style={{
                position: 'absolute',
                top: 10,
                left: 0,
                right: 0,
                alignItems: 'center',
                zIndex: 99999,
                elevation: 99999,
            }}
        >
            <TouchableOpacity
                onPress={() => setIsCollapsed(true)}
                activeOpacity={0.8}
                style={{
                    backgroundColor: '#FF8C42',
                    borderRadius: 12,
                    paddingVertical: 10,
                    paddingHorizontal: 15,
                    borderWidth: 2,
                    borderColor: '#FF6B1A',
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.2,
                    shadowRadius: 4,
                    elevation: 5,
                    maxWidth: '90%',
                }}
            >
                <TextM
                    fontSize={14}
                    color={theme.colors.white}
                    fontWeight="SemiBold"
                    textAlign="center"
                    wrap
                >
                    {isEarlyArrival
                        ? "🎯 Tu es en avance, la séance commence bientôt !"
                        : announcedArrival && !tutorBusyElsewhere
                            ? `⏰ ${announcedArrival}`
                            : "⏰ Ton tuteur ne devrait pas tarder"}
                </TextM>
                {!isEarlyArrival && (
                    <TextM
                        fontSize={12}
                        color={theme.colors.white}
                        textAlign="center"
                        style={{ marginTop: 4, opacity: 0.9 }}
                    >
                        Travaille sur ton activité en attendant !
                    </TextM>
                )}
            </TouchableOpacity>
        </Animated.View>
    )
}
